import { hasActiveCartActivity } from "./cartActivity";
import { logRecoveryEvent } from "./recoveryLog";

/**
 * Silently reload an already-open tab once a newer build has gone live, so it
 * doesn't keep running stale code indefinitely (a POS tab can sit open all day).
 *
 * We detect a new build by re-fetching the (no-store) index.html and comparing the
 * hashed `main.<hash>.js` it references against the one THIS tab actually booted
 * from. Once they differ, we watch for a safe moment — no sale in progress on
 * either the online or offline cart — and reload the instant one appears.
 * Never mid-sale, never forced, no manual click required.
 *
 * Fresh visits / new tabs already get the latest build (index.html is no-store);
 * this only covers the "tab was left open across a deploy" case.
 */

const POLL_MS = 5 * 60 * 1000;
const SAFE_RELOAD_CHECK_MS = 5 * 1000;
const WATCHING_KEY = "buildUpdateWatchingFor"; // which main.js hash we're already auto-reloading toward

// e.g. "static/js/main.a1b2c3d4.js" — the entry bundle this tab is running.
const parseMain = str => {
  const m = str && str.match(/static\/js\/main\.[^"'/]+\.js/);
  return m ? m[0] : null;
};

const OWN_MAIN = (() => {
  const el = document.querySelector('script[src*="/static/js/main."]');
  return parseMain(el && el.getAttribute("src"));
})();

const alreadyWatchingFor = latestMain => {
  try {
    return sessionStorage.getItem(WATCHING_KEY) === latestMain;
  } catch (e) {
    return false;
  }
};

let safeReloadTimer = null;

// Once a new build is detected, poll for a moment with no sale in progress
// and reload the instant one appears — a controlled update flow the merchant
// never has to notice or act on. Also the only place that ever tells a
// waiting service worker to activate (see service-worker.js's install
// handler) — its takeover is gated by this exact same safe window.
const startSafeReloadWatcher = () => {
  if (safeReloadTimer) return;
  safeReloadTimer = setInterval(() => {
    if (!hasActiveCartActivity()) {
      clearInterval(safeReloadTimer);
      logRecoveryEvent("reload-executed", { trigger: "auto", reason: "safe-window" });
      let reloaded = false;
      const reload = () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      };
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then(reg => {
          if (!reg?.waiting) {
            reload();
            return;
          }
          // Wait for the new worker to actually finish taking over before
          // reloading — posting SKIP_WAITING and reloading immediately after
          // is a race that can leave the new worker stuck "waiting" past the
          // reload. controllerchange only fires once (browser guarantee), so
          // this and the timeout below can't both call reload().
          navigator.serviceWorker.addEventListener("controllerchange", reload, { once: true });
          reg.waiting.postMessage("SKIP_WAITING");
          setTimeout(reload, 3000); // fallback if controllerchange never fires
        }).catch(reload);
      } else {
        reload();
      }
    }
  }, SAFE_RELOAD_CHECK_MS);
};

const check = async () => {
  if (!OWN_MAIN) return;
  try {
    const res = await fetch(`/?_brc=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return;
    const latestMain = parseMain(await res.text());
    if (latestMain && latestMain !== OWN_MAIN && !alreadyWatchingFor(latestMain)) {
      try {
        sessionStorage.setItem(WATCHING_KEY, latestMain);
      } catch (e) {
        /* sessionStorage unavailable — may re-detect next poll, harmless */
      }
      logRecoveryEvent("stale-version-detected", { latestMain, ownMain: OWN_MAIN });
      startSafeReloadWatcher();
    }
  } catch (e) {
    /* offline or transient — retry next tick */
  }
};

export const startBuildRefresh = () => {
  if (process.env.NODE_ENV !== "production" || !OWN_MAIN) return;
  setInterval(check, POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
};
